import React, { ChangeEvent, useEffect, useState } from 'react'
import toast from 'react-hot-toast'
import { Badge, Button, FormField, ImageBuffer, Loader, SubCover, SubHeader, SubmitModal } from '../../components'
import { postValidateSelectedFile, postValidateFirstName, postValidateLastName, postValidateSex, postValidateEmailAddress, postValidatePhoneNumber, postValidateResume } from '../../components/validations/enrollment/enrollment';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../../reducers/store';
import { actionCreateEnrollment, actionFetchEnrollmentUsers } from '../../actions/enrollment';
import { Create, PhotoRounded } from '@mui/icons-material';
import { genderOptions } from '../../utils/constants';

import './enrollment.css'

interface PostDataParams {
  selectedFile?: File | null;
  firstName?: string;
  lastName?: string;
  sex?: string;
  emailAddress?: string;
  phoneNumber?: string;
  resume?: File | null;
}

const Enrollment = () => {
  const dispatch                                        = useDispatch<AppDispatch>();
  const [savingInfo, setSavingInfo]                     = useState<boolean>(false);
  const [isButtonDisabled, setIsButtonDisabled]         = useState<boolean>(true);
  const [fileNames, setFileNames]                       = useState<{ [key: string]: string }>({});
  const [onOpen, setOnOpen]                             = useState(false);
  const [openSubMegaMenu, setOpenSubMegaMenu]           = useState<boolean>(false);
  const [errorMessage, setErrorMessage]                 = useState(null);
  const [userImg, setUserImg]                           = useState<string>();
  const [selectedFileErrors, setSelectedFileErrors]     = useState<string[]>([]);
  const [firstNameErrors, setFirstNameErrors]           = useState<string[]>([]);
  const [lastNameErrors, setLastNameErrors]             = useState<string[]>([]);
  const [sexErrors, setSexErrors]                       = useState<string[]>([]);
  const [emailAddressErrors, setEmailAddressErrors]     = useState<string[]>([]);
  const [phoneNumberErrors, setPhoneNumberErrors]       = useState<string[]>([]);
  const [resumeErrors, setResumeErrors]                 = useState<string[]>([]);
  const { getAllEnrollmentUsers, currentPage }          = useSelector((state: RootState) => state?.enrollmentList);
  
  const [postData, setPostData]           = useState<PostDataParams>({ selectedFile: null, firstName: '', lastName: '', sex: '', emailAddress: '', phoneNumber: '', resume: null})
  const [ isLoadingBtn, setIsLoadingBtn ] = useState({ selectedFile: false, firstName: false, lastName: false, sex: false, emailAddress: false, phoneNumber: false, resume: false})
  const [ isValid, setIsValid ]           = useState({ selectedFile: false, firstName: false, lastName: false, sex: false, emailAddress: false, phoneNumber: false, resume: false})

  useEffect(() => {
    dispatch(actionFetchEnrollmentUsers({currentPage})); // You can pass the page number as needed
  }, [dispatch, currentPage]);

  const useDebounce = <T,>(value: T, delay: number): T => {
    const [debounced, setDebounced] = useState(value);

    useEffect(() => {
    const handler = setTimeout(() => {
        setDebounced(value);
    }, delay);
    return () => clearTimeout(handler);
    }, [value, delay]);

    return debounced;
  };
  
  const debouncedPostData = useDebounce<PostDataParams>(postData, 500);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setIsLoadingBtn((prevState) => ({ ...prevState, [name]: true }));
    setPostData((prevState) => ({ ...prevState, [name]: value }));
  }

  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setFileNames(prev => ({
      ...prev,
      [e.target.name]: file.name,
    }));

    // Store RAW file
    setPostData(prev => ({
      ...prev,
      selectedFile: file,
    }));

    // Create preview URL
    const previewUrl = URL.createObjectURL(file);
    setUserImg(previewUrl);
  };

  const handleDocumentUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const allowedTypes = [
      "application/pdf",
      "application/msword",
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    ];

    if (!allowedTypes.includes(file.type)) {
      alert(`Invalid file type: ${file.type}`);
      return;
    }

    // Show file name
    setFileNames(prev => ({
      ...prev,
      [e.target.name]: file.name
    }));

    setPostData(prev => ({
      ...prev,
      resume: file,
    }));
  };

  const handleUserImageUpload = (e: any) => {
    const file = e.target.files[0]

    transformFile(file)
  }

  const transformFile = (file: File | null) => {
    const reader = new FileReader();
  
    if (file) {
      reader.readAsDataURL(file);
  
      reader.onloadend = () => {
        const result = reader.result;
  
        // Ensure the result is a string before updating state
        if (typeof result === 'string') {
          setUserImg(result);
        }
      };
    } else {
      setUserImg(''); // Reset the array if no file is passed
    }
  };

  useEffect(() => {
    const validateSelectedFile = () => {
      const { isValid, errors } = postValidateSelectedFile({ postData: debouncedPostData });
      setIsValid((prevState) => ({
        ...prevState,
        selectedFile: isValid,
      }));
      setSelectedFileErrors(errors);
      setIsLoadingBtn((prevState) => ({
        ...prevState,
        selectedFile: false,
      }));
      return errors;
    };

    const validateFirstName = () => {
      const { isValid, errors } = postValidateFirstName({ postData: debouncedPostData });
      setIsValid((prevState) => ({
        ...prevState,
        firstName: isValid,
      }));
      setFirstNameErrors(errors);
      setIsLoadingBtn((prevState) => ({
        ...prevState,
        firstName: false,
      }));
      return errors;
    };

    const validateLastName = () => {
      const { isValid, errors } = postValidateLastName({ postData: debouncedPostData });
      setIsValid((prevState) => ({
        ...prevState,
        lastName: isValid,
      }));
      setLastNameErrors(errors);
      setIsLoadingBtn((prevState) => ({
        ...prevState,
        lastName: false,
      }));
      return errors;
    };

    const validateSex = () => {
      const { isValid, errors } = postValidateSex({ postData: debouncedPostData });
      setIsValid((prevState) => ({
        ...prevState,
        sex: isValid,
      }));
      setSexErrors(errors);
      setIsLoadingBtn((prevState) => ({
        ...prevState,
        sex: false,
      }));
      return errors;
    };
  
    const validateEmailAddress = () => {
      const { isValid, errors } = postValidateEmailAddress({ postData: debouncedPostData, getAllEnrollmentUsers });
      setIsValid((prevState) => ({
        ...prevState,
        emailAddress: isValid,
      }));
      setEmailAddressErrors(errors)
      setIsLoadingBtn((prevState) => ({
        ...prevState,
        emailAddress: false,
      }));
      return errors;
    };

    const validatePhoneNumber = () => {
      const { isValid, errors } = postValidatePhoneNumber({ postData: debouncedPostData });
      setIsValid((prevState) => ({
        ...prevState,
        phoneNumber: isValid,
      }));
      setPhoneNumberErrors(errors)
      setIsLoadingBtn((prevState) => ({
        ...prevState,
        phoneNumber: false,
      }));
      return errors;
    };

    const validateResume = () => {
      const { isValid, errors } = postValidateResume({ postData: debouncedPostData });
      setIsValid((prevState) => ({
        ...prevState,
        resume: isValid,
      }));
      setResumeErrors(errors)
      setIsLoadingBtn((prevState) => ({
        ...prevState,
        resume: false,
      }));
      return errors;
    };
    
    const selectedFileErrors     = validateSelectedFile();
    const firstNameErrors        = validateFirstName();
    const lastNameErrors         = validateLastName();
    const sexErrors              = validateSex();
    const emailAddressErrors     = validateEmailAddress();
    const phoneNumberErrors      = validatePhoneNumber();
    const resumeErrors           = validateResume();

    setSelectedFileErrors(selectedFileErrors);
    setFirstNameErrors(firstNameErrors);
    setLastNameErrors(lastNameErrors);
    setSexErrors(sexErrors);
    setEmailAddressErrors(emailAddressErrors);
    setPhoneNumberErrors(phoneNumberErrors);
    setResumeErrors(resumeErrors);

    const hasFormErrors = selectedFileErrors.length > 0 || firstNameErrors.length > 0 || lastNameErrors.length > 0 || sexErrors.length > 0 || emailAddressErrors.length > 0 || phoneNumberErrors.length > 0 || resumeErrors.length > 0;

    setIsButtonDisabled(hasFormErrors);
  }, [debouncedPostData])

  const handleModalSubmit = () => {
    setOnOpen(true)
    setOpenSubMegaMenu(true);
  }
  
  const handleCloseCover = () => {
    setOnOpen(false)
    setOpenSubMegaMenu(false);
  }

  const handleSubmit = async(e: ChangeEvent<any>) => {
    e.preventDefault();
    handleModalSubmit()
  }

  const cancelPostCreation = () => {
    setOnOpen(false); // Close the modal
    setSavingInfo(false);
    setOpenSubMegaMenu(false);
  };

  const confirmPostCreation = async() => {
    setSavingInfo(true);
    setIsButtonDisabled(true)
    const formData = new FormData();

    Object.entries(postData).forEach(([key, value]) => {
      if (value !== null) {
        formData.append(key, value as any);
      }
    });
    const res = await dispatch(actionCreateEnrollment({postData: formData}))
    try{
      if (res.success === true) {
        // Password reset successful, show success message from response
        setErrorMessage(null); // Clear any previous error message
        setPostData({selectedFile: null, firstName: '', lastName: '', sex: '', emailAddress: '', phoneNumber: '', resume: null})
        setUserImg('');
        setFileNames({});
        setSavingInfo(false)
        setOpenSubMegaMenu(false);
        toast.success("Enrollment successfully sent!");
      } else if(res.status === 400) {
        setErrorMessage(res.data.errorMessage);
        setSavingInfo(false)
        setOpenSubMegaMenu(false);
      }
    }catch(error){
      setErrorMessage(res.status)
      setSavingInfo(false)
      setOpenSubMegaMenu(false);
    }
    setOnOpen(false); // Close the modal after confirmation
  };

  return (
    <>
      <div className='enrollment-wrapper'>
        <Badge title="Enrollment" />
        <SubHeader title="Come work with us" description="" />

        <div className='enrollment-container'>
          <div className='enrollment-form-cover'>
            <form onSubmit={handleSubmit} autoComplete="off">
              <div className='reg-image-group'>
                <div onChange={handleUserImageUpload}>
                  <FormField fileInputType name="selectedFile" selectedLabelName="Upload your Photo" accept="image/*" handleChange={handleImageUpload} selectedFileName={fileNames.selectedFile} isLoadingBtn={isLoadingBtn.selectedFile} isValid={isValid.selectedFile} errors={selectedFileErrors || []} />
                </div>
                <div className='reg-image-preview-wrapper'>
                  {userImg ? <ImageBuffer src={userImg} alt='userImg' />  : <span><PhotoRounded /></span>}
                </div>
              </div>  

              <FormField inputType type="text" labelName="First Name" name="firstName" value={postData.firstName} handleChange={handleChange} isLoadingBtn={isLoadingBtn.firstName} isValid={isValid.firstName} errors={firstNameErrors || []} />

              <FormField inputType type="text" labelName="Last Name" name="lastName" value={postData.lastName} handleChange={handleChange} isLoadingBtn={isLoadingBtn.lastName} isValid={isValid.lastName} errors={lastNameErrors || []} />

              <FormField selectType labelName="Gender" name="sex" value={postData.sex} handleChange={handleChange} options={genderOptions} isLoadingBtn={isLoadingBtn.sex} isValid={isValid.sex} errors={sexErrors || []} />

              <FormField inputType type="text" labelName="Email Address" name="emailAddress" value={postData.emailAddress} handleChange={handleChange} isLoadingBtn={isLoadingBtn.emailAddress} isValid={isValid.emailAddress} errors={emailAddressErrors || []} />

              <FormField inputType type="text" labelName="Phone Number" name="phoneNumber" maxLength={13} value={postData.phoneNumber} handleChange={handleChange} isLoadingBtn={isLoadingBtn.phoneNumber} isValid={isValid.phoneNumber} errors={phoneNumberErrors || []} />

              <FormField fileInputType selectedLabelName="Resume (Doc/Docx)" accept=".doc,.docx,.word,.pdf" name="resume" handleChange={handleDocumentUpload} selectedFileName={fileNames.resume} isLoadingBtn={isLoadingBtn.resume} isValid={isValid.resume} errors={resumeErrors || []} /> {/* changed */}

              <SubmitModal onOpen={onOpen} onClose={cancelPostCreation} onConfirm={confirmPostCreation} modalImage={Create} onClickName="Submit" prompt="Are you sure to submit enrollment?" />

              <Button onClickButton onClick={handleModalSubmit} buttonClickWrap={savingInfo ? `button-login-submitted` : `button-login-submit`} onClickName={savingInfo ? <>{<Loader />} Sending...</> : "Enroll"} isButtonDisabled={isButtonDisabled} buttonClasses={savingInfo ? ['button-disabled'] : (isButtonDisabled ? ['buttonDisabledClass'] : ['buttonEnabledClass'])} disabled={savingInfo} />

              {errorMessage && <p className='error-msg'>{errorMessage}</p>}
            </form>
          </div>
        </div>
      </div>

      {openSubMegaMenu && (
        //z-index: 3, others should be higher
        <SubCover handleCloseCover={handleCloseCover} />
      )}
    </>
  )
}

export default Enrollment